import { Box, Icon, Theme, Typography, styled } from "@outschool/backpack";
import { Activity, Section } from "@outschool/gql-backend-generated";
import { faSearch } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  searchPath,
  sectionEnrollPath,
  sectionOrActivityPath,
} from "@outschool/routes";
import {
  formatDateTimeWithWeekday,
  fullDurationStringWithStartTime,
  fullDurationStringWithoutTimes,
} from "@outschool/time";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Loading } from "@outschool/ui-legacy-component-library";
import { useIsMobile, useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import { isFlexSchedule } from "../../../shared/Activity";
import { isFuture } from "../../../shared/Section";
import { WaitlistedClassItem } from "../../hooks/useWaitlistedClasses";
import { useTimeZone } from "../../providers/TimeZoneProvider";
import Link from "../Link";
import WaitlistedClassItemActions from "./WaitlistedClassItemActions";
import WaitlistedClassItemInfo from "./WaitlistedClassItemInfo";

interface Props {
  classes: WaitlistedClassItem[];
  error?: Error | null;
  emptyStateComponent?: React.ReactNode;
  loading?: boolean;
}

const WaitlistedClassList = ({
  classes,
  error,
  loading = false,
  emptyStateComponent,
}: Props) => {
  return (
    <Box
      sx={(theme: Theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: 24,
        [theme.breakpoints.down("md")]: {
          gap: 20,
        },
      })}
    >
      {loading && <Loading />}
      {!loading &&
        classes.map(item => <ClassItem key={item.key} item={item} />)}
      {!loading && classes.length === 0 && emptyStateComponent}
      {!loading && error && (
        <Box
          flex
          sx={{
            justifyContent: "center",
            padding: "4.4rem 2rem",
            borderColor: "grey.100",
            borderStyle: "solid",
            borderWidth: "1px",
            borderRadius: "8px",
          }}
        >
          <Typography
            variant="subtitle1"
            color="grey.600"
            sx={{ textAlign: "center" }}
          >
            {error.message}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const useClassTime = (
  section: Section,
  activity: Activity,
  userTimeZone: string,
  completed?: boolean,
) => {
  const { t } = useTranslation("client\\components\\WaitlistedClassList");

  if (completed) {
    return "";
  }
  if (activity.isClub) {
    return t("Unlimited access");
  }
  if (!!activity.is_ongoing_weekly || activity.hasTeacherSchedule) {
    if (isFuture(section)) {
      return formatDateTimeWithWeekday(section.start_time, userTimeZone);
    }
    return formatDateTimeWithWeekday(
      section.nextOngoingMeeting?.start_time || section.start_time,
      userTimeZone,
    );
  }
  if (isFlexSchedule(activity)) {
    return fullDurationStringWithoutTimes(
      section.start_time,
      section.end_time,
      userTimeZone,
    );
  }
  return fullDurationStringWithStartTime(
    section.start_time,
    section.end_time,
    userTimeZone,
  );
};

interface ClassItemProps {
  item: WaitlistedClassItem;
  completed?: boolean;
}

const ClassItem = ({ item, completed }: ClassItemProps) => {
  const { timeZone } = useTimeZone();
  const isMobile = useIsMobile();

  const { section, learner } = item;
  const { activity, leader: sectionLeader } = section;
  const { details, leader: activityLeader } = activity;
  const { photo } = details;

  const classTime = useClassTime(
    section as Section,
    activity as Activity,
    timeZone,
    completed,
  );

  const leader = sectionLeader || activityLeader;
  if (!learner) {
    return null;
  }

  const ClassImage = styled("img")({ objectFit: "cover" });

  const tileLinkPath = !!item.availableSpaces
    ? sectionEnrollPath(activity, item.waitlistSeats[0]?.section)
    : sectionOrActivityPath(activity);

  return (
    <Box
      flex
      sx={[
        (theme: Theme) => ({
          gap: 25,
          overflow: "hidden",
          alignItems: "center",
          borderColor: "grey.100",
          borderStyle: "solid",
          borderWidth: "1px",
          borderRadius: "8px",
          textDecoration: "none",
          color: "unset",
          "&:hover:not(:disabled), &:active:not(:disabled), &:focus:not(:disabled)":
            {
              textDecoration: "none",
              color: "unset",
            },
          [theme.breakpoints.down("md")]: {
            gap: 0,
            flexDirection: "column",
          },
        }),
      ]}
      component={Link}
      to={tileLinkPath}
    >
      <Box
        flex
        sx={(theme: Theme) => ({
          flex: 1,
          flexDirection: "row",
          alignItems: "center",
          [theme.breakpoints.down("md")]: {
            gap: 0,
          },
        })}
      >
        <ClassImage
          src={photo ? photo : ""}
          sx={(theme: Theme) => ({
            display: "flex",
            width: 110,
            minHeight: 110,
            height: "auto",
            alignSelf: "stretch",
            [theme.breakpoints.down("md")]: {
              width: 104,
            },
          })}
        />
        <Box
          flex
          sx={(theme: Theme) => ({
            flex: 1,
            flexDirection: "column",
            padding: "1.6rem 2.4rem",
            gap: 14,
            [theme.breakpoints.down("md")]: {
              gap: 8,
              padding: "1.2rem 1.4rem 1.2rem",
            },
          })}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <Typography
              variant="h5"
              sx={(theme: Theme) => ({
                [theme.breakpoints.down("md")]: {
                  fontSize: "1.8rem",
                },
              })}
            >
              {activity.title}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={(theme: Theme) => ({
                textAlign: "right",
                whiteSpace: "nowrap",
                [theme.breakpoints.down("md")]: {
                  display: "none",
                },
              })}
            >
              {classTime}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <WaitlistedClassItemInfo
              learnerName={learner.name ?? ""}
              leaderName={leader?.name ?? ""}
              availableSpaces={item.availableSpaces ?? 0}
              activity={activity as Activity}
              classTime={classTime}
            />
            {!isMobile && <WaitlistedClassItemActions item={item} />}
          </Box>
        </Box>
      </Box>
      {isMobile && (
        <Box
          flex
          sx={{
            width: "100%",
            borderTop: "1px solid",
            borderColor: "grey.100",
            padding: "1rem 0",
          }}
        >
          <WaitlistedClassItemActions item={item} />
        </Box>
      )}
    </Box>
  );
};

export function EmptyListView({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  const Link = useLinkComponent();
  const { t } = useTranslation("client\\components\\WaitlistedClassList");
  return (
    <Box
      flex
      sx={(theme: Theme) => ({
        justifyContent: "center",
        alignItems: "center",
        flexDirection: "column",
        borderRadius: 10,
        padding: "0 1.6rem",
        height: 250,
        border: `1px solid ${theme.palette.neutral[100]}`,
        backgroundColor: theme.palette.neutral[50],
      })}
    >
      <Typography
        variant="body2"
        sx={(theme: Theme) => ({
          fontSize: "1.8rem",
          fontWeight: "medium",
          color: theme.palette.neutral[900],
          textAlign: "center",
        })}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={(theme: Theme) => ({
          fontSize: "1.8rem",
          fontWeight: "medium",
          color: theme.palette.neutral[900],
          textAlign: "center",
          marginBottom: 16,
        })}
      >
        {description}
      </Typography>

      <Link to={searchPath()}>
        <TrackedButton
          trackingLabel="parent_dashboard_find_next_class"
          variant="outlined"
          sx={{ display: "flex", gap: "1rem" }}
        >
          <Icon icon={faSearch} /> {t("Find my next class")}
        </TrackedButton>
      </Link>
    </Box>
  );
}

export default WaitlistedClassList;
